import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ApiResponse } from '@/api/http/types'
import type { CompanyCard, CompanyDetail } from '../types'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export type CompanyListParams = {
  page: number
  pageSize: number
  search: string
  status: 'all' | 'active' | 'inactive'
}

export function useCompaniesList(params: CompanyListParams) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.companies.list(tenantKey(tenantId), params),
    queryFn: async () => {
      const { data: envelope } = await apiClient.get<ApiResponse<CompanyCard[]>>(
        '/api/v1/companies',
        {
          params: {
            page: params.page,
            pageSize: params.pageSize,
            search: params.search || undefined,
            status: params.status,
          },
        },
      )
      return {
        items: envelope.data ?? [],
        total: envelope.meta?.total ?? 0,
        page: envelope.meta?.page ?? params.page,
        pageSize: envelope.meta?.per_page ?? params.pageSize,
      }
    },
    enabled: isAuthenticated,
  })
}

export function useCompany(companyId: string) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.companies.detail(tenantKey(tenantId), companyId),
    queryFn: async () => {
      const { data: envelope } = await apiClient.get<ApiResponse<CompanyDetail>>(
        `/api/v1/companies/${companyId}`,
      )
      return envelope.data
    },
    enabled: isAuthenticated && !!companyId,
  })
}
